import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Check, X, Bike, Phone, IdCard } from "lucide-react";
import { founderListDriverApplications, founderApproveDriverApp, founderRejectDriverApp } from "../lib/api";

const GREEN = "#075A43";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };

const LICENSE_LABEL = { none: "Aucun / Vélo", M: "Cyclomoteur (M)", A1: "Moto légère (A1)", A: "Moto (A)", B: "Voiture (B)", BE: "Voiture + remorque (BE)" };

export default function DriverApplications() {
  const nav = useNavigate();

  const [tab, setTab] = useState("pending");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [approveFor, setApproveFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    founderListDriverApplications()
      .then((a) => { setApps(a || []); setDenied(false); })
      .catch((e) => {
        const st = e?.response?.status;
        if (st === 401 || st === 403) setDenied(true);
        else toast.error("Chargement impossible");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (denied) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="dl-access-denied">
        <Lock size={44} color="#cbd5e1" className="mx-auto mb-4" />
        <p style={{ color: "#64748b" }}>Espace réservé au Founder / La BASE.</p>
        <button data-testid="dl-go-login" onClick={() => nav("/login")} className="mt-5 rounded-xl font-bold text-white px-6 py-3" style={{ background: GREEN }}>Se connecter</button>
      </div>
    );
  }

  const pending = apps.filter((a) => a.status === "pending");
  const shown = tab === "pending" ? pending : apps.filter((a) => a.status !== "pending");

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="dl-page">
      <h1 className="text-3xl font-extrabold" style={{ color: GREEN }}>Candidatures livreurs</h1>
      <p className="text-sm mt-1 mb-6" style={{ color: "#64748b" }}>Validez les demandes pour créer le compte livreur et sa ressource (le véhicule est fourni par La BASE).</p>

      <div className="flex gap-2 mb-6">
        {[["pending", `En attente (${pending.length})`], ["history", "Historique"]].map(([k, lbl]) => (
          <button key={k} data-testid={`dl-tab-${k}`} onClick={() => setTab(k)}
            className="px-4 py-2 rounded-full text-sm font-bold"
            style={{ background: tab === k ? GREEN : "#fff", color: tab === k ? "#fff" : GREEN, border: `1px solid ${GREEN}` }}>
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-40 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} data-testid="dl-loading" />
      ) : (
        <div className="grid gap-4">
          {shown.length === 0 && <p style={{ color: "#64748b" }} data-testid="dl-empty">Aucune candidature{tab === "pending" ? " en attente" : ""}.</p>}
          {shown.map((a) => (
            <div key={a.id} data-testid={`dl-app-${a.id}`} className="rounded-2xl p-4" style={{ border: "1px solid #f0f0f0" }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-extrabold" style={{ color: "#1f2937" }}>{a.display_name}</p>
                  <p className="text-sm" style={{ color: "#64748b" }}>@{a.username}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-2" style={{ color: "#64748b" }}>
                    <span className="flex items-center gap-1"><Phone size={14} /> {a.phone || "—"}</span>
                    <span className="flex items-center gap-1"><IdCard size={14} /> Permis {LICENSE_LABEL[a.license_type] || a.license_type}</span>
                    <span className="flex items-center gap-1"><Bike size={14} /> Véhicule perso : <b>{a.has_personal_vehicle ? "oui" : "non"}</b></span>
                  </div>
                </div>
                <StatusBadge status={a.status} />
              </div>
              {a.status === "pending" && (
                <div className="flex gap-2 mt-4">
                  <button data-testid={`dl-approve-${a.id}`} onClick={() => setApproveFor(a)}
                    className="rounded-xl font-bold text-white px-4 py-2 flex items-center gap-2" style={{ background: GREEN }}>
                    <Check size={16} /> Approuver
                  </button>
                  <button data-testid={`dl-reject-${a.id}`} onClick={async () => {
                    await founderRejectDriverApp(a.id, "Non retenu"); toast.success("Candidature refusée"); load();
                  }} className="rounded-xl font-bold px-4 py-2 flex items-center gap-2" style={{ background: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}>
                    <X size={16} /> Refuser
                  </button>
                </div>
              )}
              {a.status === "approved" && a.vehicle_type && (
                <p className="text-sm mt-3" style={{ color: GREEN }}>Véhicule attribué : <b>{a.vehicle_type}</b></p>
              )}
            </div>
          ))}
        </div>
      )}

      {approveFor && <ApproveModal app={approveFor} onClose={() => setApproveFor(null)}
        onDone={() => { setApproveFor(null); toast.success("Livreur approuvé — compte créé"); load(); }} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = { pending: ["#fef3c7", "#b45309", "En attente"], approved: ["#dcfce7", GREEN, "Approuvée"], rejected: ["#fee2e2", "#ef4444", "Refusée"] };
  const [bg, fg, lbl] = map[status] || map.pending;
  return <span className="text-xs font-bold px-2.5 py-1 rounded-full h-fit" style={{ background: bg, color: fg }}>{lbl}</span>;
}

function ApproveModal({ app, onClose, onDone }) {
  const [vehicle, setVehicle] = useState("scooter");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await founderApproveDriverApp(app.id, vehicle); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,.45)" }} onClick={onClose}>
      <div data-testid="dl-approve-modal" className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-lg font-extrabold" style={{ color: GREEN }}>Approuver — {app.display_name}</p>
          <button onClick={onClose} data-testid="dl-modal-close"><X size={20} color="#94a3b8" /></button>
        </div>
        <label className="text-xs font-bold" style={{ color: "#64748b" }}>Véhicule fourni par La BASE</label>
        <select data-testid="dl-approve-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1 mb-4" style={field}>
          <option value="bike">Vélo</option>
          <option value="scooter">Scooter</option>
          <option value="car">Voiture</option>
        </select>
        <button data-testid="dl-approve-confirm" onClick={submit} disabled={busy} className="w-full rounded-xl font-bold text-white h-12" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>
          {busy ? "…" : "Confirmer et créer le compte"}
        </button>
      </div>
    </div>
  );
}
